import { useEffect, useRef, type ReactNode, type Ref } from 'react';
import './kit.css';

/**
 * Small form pieces the Phase 7 Ops pages share (money dialogs, notes, references). They sit on
 * the back-office kit (.dialog, .field, .input-affix, .err-pill) and add nothing new to it.
 */

/** A modal on <dialog> (focus trap, Esc and an inert page for free), opened by `open`. */
export function Modal({ open, labelledBy, onClose, wide, children }: { open: boolean; labelledBy: string; onClose(): void; wide?: boolean; children: ReactNode }) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (open && !d.open) d.showModal();
    else if (!open && d.open) d.close();
  }, [open]);
  return (
    <dialog ref={ref} className={wide ? 'dialog ops-dialog-wide' : 'dialog'} aria-labelledby={labelledBy} onClose={onClose}>
      {open ? children : null}
    </dialog>
  );
}

/** A right-hand sheet on <dialog>, for a record's detail (Customers). */
export function Drawer({ open, labelledBy, onClose, children }: { open: boolean; labelledBy: string; onClose(): void; children: ReactNode }) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (open && !d.open) d.showModal();
    else if (!open && d.open) d.close();
  }, [open]);
  return (
    <dialog ref={ref} className="dialog ops-drawer" aria-labelledby={labelledBy} onClose={onClose}>
      {open ? children : null}
    </dialog>
  );
}

export function FieldError({ id, children }: { id: string; children?: ReactNode }) {
  if (!children) return null;
  return (
    <span id={id} className="pill st-error err-pill" style={{ whiteSpace: 'normal' }}>
      {children}
    </span>
  );
}

/** A peso amount field: "₱" affix, decimal keypad, its own help and error lines. */
export function MoneyField({
  id,
  label,
  value,
  onChange,
  error,
  help,
  inputRef,
}: {
  id: string;
  label: string;
  value: string;
  onChange(v: string): void;
  error?: string;
  help?: ReactNode;
  inputRef?: Ref<HTMLInputElement>;
}) {
  const describedBy = [help ? `${id}-help` : null, error ? `${id}-err` : null].filter(Boolean).join(' ') || undefined;
  return (
    <div className="field">
      <label htmlFor={id}>{label}</label>
      <div className="input-affix" data-invalid={error ? 'true' : undefined}>
        <span aria-hidden="true" className="t-mono">
          ₱
        </span>
        <input id={id} ref={inputRef} inputMode="decimal" autoComplete="off" value={value} onChange={(e) => onChange(e.target.value)} aria-invalid={error ? true : undefined} aria-describedby={describedBy} />
      </div>
      {help ? (
        <span id={`${id}-help`} className="help">
          {help}
        </span>
      ) : null}
      <FieldError id={`${id}-err`}>{error}</FieldError>
    </div>
  );
}

/** A one-line or multi-line text field with help and error. */
export function TextField({
  id,
  label,
  value,
  onChange,
  error,
  help,
  multiline,
  maxLength = 200,
  placeholder,
  inputRef,
}: {
  id: string;
  label: string;
  value: string;
  onChange(v: string): void;
  error?: string;
  help?: ReactNode;
  multiline?: boolean;
  maxLength?: number;
  placeholder?: string;
  inputRef?: Ref<HTMLInputElement & HTMLTextAreaElement>;
}) {
  const describedBy = [help ? `${id}-help` : null, error ? `${id}-err` : null].filter(Boolean).join(' ') || undefined;
  const common = {
    id,
    className: 'input',
    value,
    maxLength,
    placeholder,
    autoComplete: 'off',
    'aria-invalid': error ? true : undefined,
    'aria-describedby': describedBy,
  } as const;
  return (
    <div className="field">
      <label htmlFor={id}>{label}</label>
      {multiline ? <textarea ref={inputRef} {...common} onChange={(e) => onChange(e.target.value)} /> : <input ref={inputRef} {...common} onChange={(e) => onChange(e.target.value)} />}
      {help ? (
        <span id={`${id}-help`} className="help">
          {help}
        </span>
      ) : null}
      <FieldError id={`${id}-err`}>{error}</FieldError>
    </div>
  );
}

/** Cancel + the one confirm button, right-aligned, as on every back-office dialog. */
export function DialogActions({ busy, confirm, busyLabel, danger, disabled, onCancel, cancelLabel = 'Cancel' }: { busy: boolean; confirm: ReactNode; busyLabel: string; danger?: boolean; disabled?: boolean; onCancel(): void; cancelLabel?: string }) {
  return (
    <div className="row ops-dialog-actions">
      <button type="button" className="btn" onClick={onCancel}>
        {cancelLabel}
      </button>
      <button type="submit" className={danger ? 'btn btn-danger' : 'btn btn-primary'} disabled={busy || disabled} aria-busy={busy || undefined}>
        {busy ? busyLabel : confirm}
      </button>
    </div>
  );
}

/** A stale-data line under a panel that failed to refresh but still shows its last answer. */
export function StaleNote({ show, what = 'this' }: { show: boolean; what?: string }) {
  if (!show) return null;
  return (
    <p className="pill st-warning err-pill" role="status" style={{ whiteSpace: 'normal' }}>
      Couldn't refresh {what}. Showing the last update.
    </p>
  );
}
